import { Button, Input, Select, toast } from "@gotts.ai/ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import { useEvmBackend } from "../../context";
import { useRpcRequest } from "../../hooks/useRpcRequest";

import { ConfirmModal } from "./ConfirmModal";
import { DEFI_ADDRESS_PRESETS } from "./constants";

import type { ChainControlSharedProps } from "./types";

function isValidAddress(addr: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(addr);
}

interface ImpersonateCardProps extends ChainControlSharedProps {
  activeImpersonation: string | null;
  onImpersonationChange: (address: string | null) => void;
}

export function ImpersonateCard({
  loading,
  withLoading,
  onAction,
  activeImpersonation,
  onImpersonationChange,
}: ImpersonateCardProps) {
  const { request } = useRpcRequest();
  const { accounts } = useEvmBackend();

  const accountOptions = useMemo(
    () => [
      ...accounts.map((a) => ({
        label: `${a.label} (${a.address.slice(0, 6)}...${a.address.slice(-4)})`,
        value: a.address,
      })),
      { label: "Custom Address", value: "__other__" },
    ],
    [accounts],
  );
  const [accountSelect, setAccountSelect] = useState<string>("__other__");
  const [customAddress, setCustomAddress] = useState("");
  const [showDescription, setShowDescription] = useState(false);

  // ConfirmModal state
  const [confirmOpen, setConfirmOpen] = useState(false);

  const address = accountSelect === "__other__" ? customAddress : accountSelect;

  const addressValid = address ? isValidAddress(address) : false;

  function handleImpersonateClick() {
    if (!address || !addressValid) return;
    setConfirmOpen(true);
  }

  async function handleImpersonateConfirm() {
    setConfirmOpen(false);
    if (!address || !addressValid) return;

    await withLoading("impersonate", async () => {
      await request("anvil_impersonateAccount", [address]);
      onImpersonationChange(address);
      toast.success(
        `Impersonating ${address.slice(0, 6)}...${address.slice(-4)}`,
      );
      onAction?.(`Impersonating ${address.slice(0, 6)}...`);
    });
  }

  async function handleStopImpersonating() {
    if (!activeImpersonation) return;
    await withLoading("stopImpersonate", async () => {
      await request("anvil_stopImpersonatingAccount", [activeImpersonation]);
      toast.success(
        `Stopped impersonating ${activeImpersonation.slice(0, 6)}...${activeImpersonation.slice(-4)}`,
      );
      onImpersonationChange(null);
      onAction?.("Stopped impersonating");
    });
  }

  return (
    <div className="space-y-4">
      {activeImpersonation && (
        <div className="bg-[var(--color-warning)]/8 flex items-center gap-2 rounded-sm px-3 py-2">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--color-warning)]" />
          <span className="font-mono text-xs text-[var(--color-warning)]">
            {activeImpersonation.slice(0, 10)}...
            {activeImpersonation.slice(-6)}
          </span>
        </div>
      )}

      <Select
        options={accountOptions}
        value={accountSelect}
        onValueChange={setAccountSelect}
        placeholder="Select account"
      />

      {accountSelect === "__other__" && (
        <>
          <Input
            type="text"
            placeholder="0x... address to impersonate"
            value={customAddress}
            onChange={(e) => {
              setCustomAddress(e.target.value);
            }}
            className={`w-full ${
              customAddress
                ? isValidAddress(customAddress)
                  ? "border-[var(--color-success)]/50"
                  : "border-[var(--color-error)]/50"
                : ""
            }`}
          />
          {/* DeFi address presets */}
          <div className="flex flex-wrap gap-1.5">
            {DEFI_ADDRESS_PRESETS.map((p) => (
              <button
                key={p.address}
                type="button"
                className="chip-preset"
                onClick={() => {
                  setCustomAddress(p.address);
                }}
                disabled={loading !== null}
                title={p.address}
              >
                {p.label}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="flex gap-2">
        {activeImpersonation ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleStopImpersonating}
            loading={loading === "stopImpersonate"}
            disabled={loading !== null}
            className="flex-1"
          >
            Stop Impersonating
          </Button>
        ) : (
          <Button
            variant="gold"
            size="sm"
            onClick={handleImpersonateClick}
            loading={loading === "impersonate"}
            disabled={loading !== null || !address || !addressValid}
            className="btn-amber flex-1"
          >
            Impersonate
          </Button>
        )}
      </div>

      {/* Description — collapsed by default */}
      <button
        type="button"
        onClick={() => {
          setShowDescription((v) => !v);
        }}
        className="flex items-center gap-1.5 text-[10px] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-dim)]"
      >
        {showDescription ? (
          <ChevronDown size={11} />
        ) : (
          <ChevronRight size={11} />
        )}
        What does impersonation do?
      </button>
      {showDescription && (
        <p className="text-[10px] leading-relaxed text-[var(--color-text-muted)]">
          Send transactions as any address on the local Anvil chain without
          needing the private key. Useful for testing interactions with specific
          accounts or contracts.
        </p>
      )}

      {/* Confirm modal */}
      <ConfirmModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
        }}
        onConfirm={handleImpersonateConfirm}
        title="Impersonate Account"
        description={`Impersonate ${address}? This allows sending transactions as this address on the local Anvil chain.`}
        confirmLabel="Impersonate"
        confirmVariant="warning"
      />
    </div>
  );
}
